/**
 * Polling hook for standalone harness tasks — peer to
 * `useMissionsPoll`, drives `useHarnTasksStore` against
 * `GET /api/harn/tasks` on a dual cadence.
 *
 * Cadence + Page-Visibility semantics mirror `useMissionsPoll` exactly:
 *   - 5s when ANY task is in an ACTIVE status
 *     (queued | dispatched | running).
 *   - 30s otherwise.
 *   - Visibility-pause kicks in after 60s hidden; visible-flip wakes
 *     it with an immediate fetch.
 *   - Cleanup-safe via `cancelled` flag.
 *   - Inflight dedup keyed by caseId.
 *
 * Cancel flow mirrors mission-cancel:
 *   1. `markCanceling(taskId)` (optimistic)
 *   2. `cancelTask(taskId)` → `{cancelled, alreadyTerminal}`
 *   3. Drill in via `getTask(taskId)` to reconcile terminal state
 *   4. 404 on drill-in → `removeTask` (defensive — task gone)
 *   5. `alreadyTerminal === true` is NOT an error; surfaces as a
 *      soft poll-error message + the next poll reconciles state.
 *
 * Pure factory `createHarnTasksPoller` for unit testing without
 * `renderHook` per lexide convention. The React hook is a thin
 * `useEffect` wrapper.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §G.3 + §J.S2.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import {
  cancelTask as apiCancelTaskDefault,
  getTask as getTaskDefault,
  HarnApiError,
  listTasks as listTasksDefault,
  type ListTasksFilters,
  type HarnTaskRecord,
  type HarnTaskSummary,
} from "@/api/lx-harn";
import {
  selectHasActiveTasks,
  selectTasksByCase,
  useHarnTasksStore,
  type HarnTasksStoreState,
} from "@/stores/useHarnTasksStore";

/** Cross-case page size — the server's maximum, so the pane's authoritative
 *  window covers everything it could be holding from earlier sessions. */
export const HARN_CROSS_CASE_LIMIT = 500;

export const HARN_POLL_FAST_MS = 5_000;
export const HARN_POLL_SLOW_MS = 30_000;
export const HARN_VIS_PAUSE_MS = 60_000;

// ─── Pure factory ──────────────────────────────────────────────────────

export interface HarnPollerDeps {
  listTasks?: typeof listTasksDefault;
  getTask?: typeof getTaskDefault;
  cancelTaskApi?: typeof apiCancelTaskDefault;
  store?: {
    hydrateFromList: HarnTasksStoreState["hydrateFromList"];
    upsertTask: HarnTasksStoreState["upsertTask"];
    markCanceling: HarnTasksStoreState["markCanceling"];
    removeTask: HarnTasksStoreState["removeTask"];
    setPollError: HarnTasksStoreState["setPollError"];
    setLastPolledAt: HarnTasksStoreState["setLastPolledAt"];
    hasActiveTasks: () => boolean;
  };
  visibility?: {
    isVisible: () => boolean;
    addListener: (handler: () => void) => () => void;
  };
  now?: () => number;
  setTimer?: (cb: () => void, ms: number) => unknown;
  clearTimer?: (token: unknown) => void;
}

export interface HarnPoller {
  start: () => void;
  dispose: () => void;
  refetch: () => Promise<void>;
  cancelTask: (taskId: string) => Promise<void>;
  notifyVisibilityChange: () => void;
  __getState: () => {
    cancelled: boolean;
    timerToken: unknown;
    hiddenSinceMs: number | null;
    started: boolean;
  };
}

export interface CreateHarnPollerOptions extends HarnPollerDeps {
  caseId: string | null;
}

function defaultVisibility(): NonNullable<HarnPollerDeps["visibility"]> {
  if (typeof document === "undefined") {
    return { isVisible: () => true, addListener: () => () => undefined };
  }
  return {
    isVisible: () => document.visibilityState !== "hidden",
    addListener: (handler) => {
      document.addEventListener("visibilitychange", handler);
      return () => document.removeEventListener("visibilitychange", handler);
    },
  };
}

function defaultStore(): NonNullable<HarnPollerDeps["store"]> {
  return {
    hydrateFromList: (summaries, opts) =>
      useHarnTasksStore.getState().hydrateFromList(summaries, opts),
    upsertTask: (record) => useHarnTasksStore.getState().upsertTask(record),
    markCanceling: (id) => useHarnTasksStore.getState().markCanceling(id),
    removeTask: (id) => useHarnTasksStore.getState().removeTask(id),
    setPollError: (msg) => useHarnTasksStore.getState().setPollError(msg),
    setLastPolledAt: (ts) => useHarnTasksStore.getState().setLastPolledAt(ts),
    hasActiveTasks: () => selectHasActiveTasks(useHarnTasksStore.getState()),
  };
}

export function createHarnTasksPoller(
  opts: CreateHarnPollerOptions,
): HarnPoller {
  const listTasksImpl = opts.listTasks ?? listTasksDefault;
  const getTaskImpl = opts.getTask ?? getTaskDefault;
  const cancelImpl = opts.cancelTaskApi ?? apiCancelTaskDefault;
  const store = opts.store ?? defaultStore();
  const vis = opts.visibility ?? defaultVisibility();
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer =
    opts.clearTimer ??
    ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const caseId = opts.caseId;

  let cancelled = false;
  let timerToken: unknown = null;
  let hiddenSinceMs: number | null = null;
  let started = false;
  let removeVisListener: (() => void) | null = null;
  let inFlight: Promise<void> | null = null;

  // Explicit limit for the CROSS-CASE pane. The server defaults to the
  // 100 most-recent (`harn-service/src/router.ts:312`, max 500), while
  // the store persists slots across sessions — so a default-sized page
  // left older persisted rows permanently unreconciled and visible. A
  // wider window lets `hydrateFromList` actually reconcile what it shows.
  const filter: ListTasksFilters = caseId
    ? { caseId }
    : { limit: HARN_CROSS_CASE_LIMIT };

  const fetchOnce = async (): Promise<void> => {
    if (inFlight) return inFlight;
    const p = (async () => {
      try {
        const { tasks: summaries }: { tasks: HarnTaskSummary[] } =
          await listTasksImpl(filter);
        if (cancelled) return;
        store.hydrateFromList(summaries, { caseId: caseId ?? undefined });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "harn poll failed";
        store.setPollError(msg);
        store.setLastPolledAt(now());
      }
    })();
    inFlight = p;
    try {
      await p;
    } finally {
      inFlight = null;
    }
  };

  const tick = async (): Promise<void> => {
    if (cancelled) return;
    if (!vis.isVisible()) {
      if (hiddenSinceMs === null) hiddenSinceMs = now();
      const hiddenFor = now() - hiddenSinceMs;
      if (hiddenFor > HARN_VIS_PAUSE_MS) {
        if (timerToken !== null) clearTimer(timerToken);
        timerToken = setTimer(() => void tick(), HARN_POLL_SLOW_MS);
        return;
      }
    } else {
      hiddenSinceMs = null;
    }
    await fetchOnce();
    if (cancelled) return;
    const ms = store.hasActiveTasks() ? HARN_POLL_FAST_MS : HARN_POLL_SLOW_MS;
    if (timerToken !== null) clearTimer(timerToken);
    timerToken = setTimer(() => void tick(), ms);
  };

  const notifyVisibilityChange = (): void => {
    if (cancelled) return;
    if (vis.isVisible()) {
      hiddenSinceMs = null;
      if (timerToken !== null) {
        clearTimer(timerToken);
        timerToken = null;
      }
      void tick();
    } else {
      if (hiddenSinceMs === null) hiddenSinceMs = now();
    }
  };

  return {
    start: () => {
      if (started || cancelled) return;
      started = true;
      removeVisListener = vis.addListener(notifyVisibilityChange);
      void tick();
    },
    dispose: () => {
      cancelled = true;
      if (timerToken !== null) {
        clearTimer(timerToken);
        timerToken = null;
      }
      if (removeVisListener) {
        removeVisListener();
        removeVisListener = null;
      }
    },
    refetch: () => fetchOnce(),
    cancelTask: async (taskId: string) => {
      store.markCanceling(taskId);
      try {
        const result = await cancelImpl(taskId);
        if (result.alreadyTerminal) {
          // Not an error; surface as a soft poll-error so the pane
          // user knows the row was already gone. The next poll
          // reconciles state.
          store.setPollError(
            `Task ${taskId} was already finished; nothing to cancel.`,
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "cancel failed";
        store.setPollError(msg);
        throw err;
      }
      try {
        const rec: HarnTaskRecord = await getTaskImpl(taskId);
        store.upsertTask(rec);
      } catch (err) {
        if (err instanceof HarnApiError && err.status === 404) {
          store.removeTask(taskId);
          return;
        }
        const msg =
          err instanceof Error ? err.message : "post-cancel drill-in failed";
        store.setPollError(msg);
      }
    },
    notifyVisibilityChange,
    __getState: () => ({
      cancelled,
      timerToken,
      hiddenSinceMs,
      started,
    }),
  };
}

// ─── React hook (thin wrapper) ─────────────────────────────────────────

export interface UseHarnTasksPollOptions {
  caseId?: string | null;
}

export interface UseHarnTasksPollResult {
  tasks: ReturnType<typeof selectTasksByCase>;
  isLoading: boolean;
  error: string | null;
  lastPolledAt: number | null;
  refetch: () => Promise<void>;
  cancelTask: (taskId: string) => Promise<void>;
}

export function useHarnTasksPoll(
  opts: UseHarnTasksPollOptions = {},
): UseHarnTasksPollResult {
  const caseId = opts.caseId ?? null;
  const tasks = useHarnTasksStore(
    useShallow((s) => selectTasksByCase(s, caseId ?? undefined)),
  );
  const error = useHarnTasksStore((s) => s.pollError);
  const lastPolledAt = useHarnTasksStore((s) => s.lastPolledAt);
  const [isLoading, setIsLoading] = useState<boolean>(tasks.length === 0);

  const pollerRef = useRef<HarnPoller | null>(null);

  useEffect(() => {
    setIsLoading(true);
    useHarnTasksStore.getState().setPollError(null);

    const poller = createHarnTasksPoller({ caseId });
    pollerRef.current = poller;
    poller.start();
    const unsub = useHarnTasksStore.subscribe((state, prev) => {
      if (state.lastPolledAt !== prev.lastPolledAt) {
        setIsLoading(false);
      }
    });
    return () => {
      poller.dispose();
      pollerRef.current = null;
      unsub();
    };
  }, [caseId]);

  const refetch = useCallback(async () => {
    await pollerRef.current?.refetch();
  }, []);

  const cancelTask = useCallback(async (taskId: string) => {
    await pollerRef.current?.cancelTask(taskId);
  }, []);

  return { tasks, isLoading, error, lastPolledAt, refetch, cancelTask };
}
